import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Images, Crop } from "lucide-react";
import { StockImagePicker, type StockCategory } from "./StockImagePicker";
import { ImageCropModal } from "./ImageCropModal";

interface Props {
  value: string;
  onChange: (url: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  label?: string;
  stockCategory?: StockCategory;
  aspect?: number;
  enableCrop?: boolean;
}

const MAX_PX = 1600;
const JPEG_QUALITY = 0.88;

async function compressImage(file: File, maxPx = MAX_PX, quality = JPEG_QUALITY): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        const scale = maxPx / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality,
      );
    };
    // If the browser can't decode the file (e.g. an iPhone HEIC, or an unusual
    // format), DON'T fail the whole upload — fall back to uploading the original
    // bytes. Compression is an optimisation, not a gate.
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

async function uploadFile(file: File): Promise<string> {
  const token = localStorage.getItem("ayrshire_token");
  if (!token) throw new Error("Not authenticated");
  const reqRes = await fetch("/api/storage/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
  });
  if (!reqRes.ok) throw new Error(reqRes.status === 401 ? "Please sign in again to upload" : `Couldn't start upload (${reqRes.status})`);
  const { uploadURL, objectPath, finalizeToken } = await reqRes.json() as { uploadURL: string; objectPath: string; finalizeToken: string };
  const uploadRes = await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream", Authorization: `Bearer ${token}` } });
  if (!uploadRes.ok) throw new Error(`Upload rejected (${uploadRes.status})`);
  const finalizeRes = await fetch("/api/storage/uploads/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ finalizeToken }),
  });
  if (!finalizeRes.ok) throw new Error(`Couldn't finalise upload (${finalizeRes.status})`);
  return `/api/storage/objects${objectPath.startsWith("/objects") ? objectPath.slice(8) : objectPath}`;
}

export { uploadFile };

export function ImageUpload({ value, onChange, onUploadingChange, label = "Upload Image", stockCategory, aspect = 16 / 9, enableCrop = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const setUploadingState = (v: boolean) => {
    setUploading(v);
    onUploadingChange?.(v);
  };
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    if (raw.size > 20 * 1024 * 1024) { setError("Image must be under 20MB"); return; }
    setError("");
    if (inputRef.current) inputRef.current.value = "";

    if (enableCrop) {
      const objectUrl = URL.createObjectURL(raw);
      setCropSrc(objectUrl);
      setPendingFile(raw);
      return;
    }

    await doUpload(raw);
  };

  const doUpload = async (file: File) => {
    setUploadingState(true);
    try {
      const compressed = await compressImage(file);
      const url = await uploadFile(compressed);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setUploadingState(false);
    }
  };

  const handleCropDone = async (blob: Blob) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingFile(null);
    setUploadingState(true);
    try {
      const file = new File([blob], (pendingFile?.name ?? "image").replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
      const url = await uploadFile(file);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setUploadingState(false);
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingFile(null);
  };

  const handleCropExisting = () => {
    if (!value) return;
    setCropSrc(value);
    setPendingFile(null);
  };

  return (
    <div className="space-y-2">
      {cropSrc && (
        <ImageCropModal
          imageUrl={cropSrc}
          aspect={aspect}
          onDone={(blob) => void handleCropDone(blob)}
          onCancel={handleCropCancel}
        />
      )}

      {value && (
        <div className="relative w-full max-w-xs">
          <img src={value} alt="Preview" className="w-full h-40 object-cover rounded-lg border border-slate-200" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow border border-slate-200 hover:bg-red-50 text-slate-500 hover:text-red-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => void handleFile(e)} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="gap-2"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading…" : value ? "Change Image" : label}
        </Button>
        {enableCrop && value && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCropExisting}
            className="gap-2"
          >
            <Crop className="w-4 h-4" />
            Crop
          </Button>
        )}
        {stockCategory && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            className="gap-2"
          >
            <Images className="w-4 h-4" />
            Choose from Gallery
          </Button>
        )}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {stockCategory && (
        <StockImagePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          category={stockCategory}
          onSelect={onChange}
          selected={value}
        />
      )}
    </div>
  );
}
